import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { isCompareGraphOverlay } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { shareView } from 'components/Graph/commands/shareView';
import { ShareIcon } from 'components/icons/ShareIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import { useLogError } from 'containers/ErrorBoundary/useLogError';
import { flow } from 'fp-ts/lib/function';
import type { FC } from 'react';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useGraphContextSelectors } from '../GraphContext';
import { GraphToolbarButton } from '../GraphToolbar/GraphToolbarButton';
import { ShareViewDialogBody } from './ShareViewDialogBody';

interface Props {
  disabled: boolean;
}

const SHARE_DIALOG = 'shareViewDialog';

export const ShareView: FC<Props> = (props) => {
  const [loadingState, setLoadingState] = useLoadingState();
  const logError = useLogError();
  const ctxProps = useGraphContextSelectors({
    dispatch: prop('dispatch'),
    compareActive: flow(prop('overlay'), isCompareGraphOverlay),
    sharedViewId: prop('sharedViewId'),
  });
  const msg = useLocale();
  const { showNotification } = useNotifications();
  const shareDialog = useConfirmDialog({ name: SHARE_DIALOG });

  const handleShareViewClick = async () => {
    if (ctxProps.sharedViewId) {
      shareDialog.toggleConfirmDialog();
      return;
    }

    if (loadingState !== 'LOADING') {
      try {
        await withLoadingIndicator(setLoadingState)(async () => {
          await ctxProps.dispatch(shareView);
          shareDialog.toggleConfirmDialog();
        });
      } catch (e) {
        logError(e, 'GRAPH');
        showNotification({
          type: 'danger',
          message: msg.canvasGraph.commands.saveView.cannotSaveView,
        });
      }
    }
  };

  const shareDialogMessages = {
    btnConfirm: msg.canvasGraph.commands.shareView.btnConfirm,
    header: msg.canvasGraph.commands.shareView.title,
  };

  return (
    <>
      <ConfirmDialog
        closable={false}
        bodyComponent={
          <ShareViewDialogBody sharedViewId={ctxProps.sharedViewId} />
        }
        id={SHARE_DIALOG}
        msg={shareDialogMessages}
        onClickConfirm={shareDialog.closeConfirmDialog}
      />
      <GraphToolbarButton
        disabled={props.disabled}
        disabledTitle={
          props.disabled
            ? msg.canvasGraph.graphToolbar.buttons.selectDataToDisplay
            : msg.canvasGraph.graphToolbar.buttons.shareViewDisabledCompare
        }
        onClick={handleShareViewClick}
        title={msg.canvasGraph.graphToolbar.buttons.shareView}
      >
        {loadingState === 'LOADING' ? (
          <div className='graph-save-view__spinner-wrapper'>
            <i className='fa-regular fa-spinner fa-spin fa-fw' />
          </div>
        ) : (
          <ShareIcon height={17} width={16} title='Share view' />
        )}
      </GraphToolbarButton>
    </>
  );
};
